/**
 * Getting a code off a mould and into the app.
 *
 * Three ways in, because a plant has three kinds of person holding three kinds
 * of hardware and only one of them has a working camera:
 *
 *   1. The camera. What most people mean by "scan".
 *   2. A handheld scanner. These emulate a keyboard - they type the code and
 *      press Enter - so they need no camera and no permission prompt, and they
 *      are what the store room actually owns.
 *   3. Typing it. The QR plate gets scratched, painted over or ground off, and
 *      when it does the whole system must not stop.
 *
 * All three are live at once. Nobody has to choose a mode first, which matters
 * because the person who has to choose is the person whose scanner just failed.
 *
 * Camera decoding prefers the browser's own `BarcodeDetector`: no download, and
 * it hands off to the platform. Where that is missing, zxing is fetched on
 * demand - lazily, so the many devices that never need it do not carry it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CameraOff, Flashlight, Keyboard, ScanLine } from 'lucide-react';
import { Button } from '../../components/ui/button.js';
import { Input } from '../../components/ui/field.js';

/** Ignore a repeat of the same code within this long - decoders fire continuously. */
const REPEAT_MS = 2500;

/**
 * A handheld scanner types this fast; a person does not.
 *
 * The gap between characters is what separates the two. 40ms is comfortably
 * above what any wedge produces and far below what a human hand manages, so
 * neither is mistaken for the other.
 */
const WEDGE_GAP_MS = 40;
const WEDGE_MIN_LENGTH = 3;

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}
type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

function nativeDetector(): BarcodeDetectorCtor | null {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  return typeof ctor === 'function' ? ctor : null;
}

export interface CodeReaderProps {
  /** Fired once per accepted code, already trimmed. */
  onCode: (code: string) => void;
  /** Paused while a result is on screen, so the camera does not re-fire behind it. */
  paused?: boolean;
}

export function CodeReader({ onCode, paused = false }: CodeReaderProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopDecodeRef = useRef<(() => void) | null>(null);
  const lastRef = useRef<{ code: string; at: number }>({ code: '', at: 0 });

  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchable, setTorchable] = useState(false);
  const [manual, setManual] = useState('');
  const [wedged, setWedged] = useState(false);

  /** One gate for every input path: trim, drop repeats, hand it on. */
  const accept = useCallback(
    (raw: string) => {
      const code = raw.trim();
      if (!code) return;
      const now = Date.now();
      if (code === lastRef.current.code && now - lastRef.current.at < REPEAT_MS) return;
      lastRef.current = { code, at: now };
      onCode(code);
    },
    [onCode],
  );

  // --- 2. the handheld scanner ---
  //
  // Listens on the window rather than an input, so it works wherever the focus
  // happens to be. A wedge is recognised by its speed: characters arriving
  // faster than a person can type, terminated by Enter.
  useEffect(() => {
    let buffer = '';
    let lastKeyAt = 0;

    const onKeyDown = (e: KeyboardEvent) => {
      // Someone typing into a field is typing, not scanning - unless it is the
      // manual box, which is the one place a wedge is also welcome.
      const target = e.target as HTMLElement | null;
      const inField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;
      if (inField && target?.getAttribute('data-scan-manual') !== 'true') return;

      const now = Date.now();
      const fast = now - lastKeyAt < WEDGE_GAP_MS;
      lastKeyAt = now;

      if (e.key === 'Enter') {
        if (buffer.length >= WEDGE_MIN_LENGTH) {
          e.preventDefault();
          setWedged(true);
          accept(buffer);
          setManual('');
        }
        buffer = '';
        return;
      }

      if (e.key.length !== 1) return;
      buffer = fast ? buffer + e.key : e.key;
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [accept]);

  // --- 1. the camera ---

  const stopCamera = useCallback(() => {
    stopDecodeRef.current?.();
    stopDecodeRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
    setTorchOn(false);
    setTorchable(false);
  }, []);

  // Always release the camera on unmount. A stream left running holds the
  // hardware and leaves the recording light on, which on a shared handheld
  // reads as the app watching the room.
  useEffect(() => stopCamera, [stopCamera]);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('This browser cannot open a camera. Use a handheld scanner or type the code.');
      return;
    }
    if (!window.isSecureContext) {
      // Worth saying plainly: it looks like a broken camera and is not one.
      setCameraError('The camera needs a secure (https) connection. Type the code instead.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        // iOS refuses to play inline without both of these.
        video.setAttribute('playsinline', 'true');
        video.muted = true;
        await video.play().catch(() => undefined);
      }

      const track = stream.getVideoTracks()[0];
      const capabilities = track?.getCapabilities?.() as { torch?: boolean } | undefined;
      setTorchable(Boolean(capabilities?.torch));

      startDecoding(stream);
    } catch (error) {
      const name = error instanceof Error ? error.name : '';
      setCameraError(
        name === 'NotAllowedError'
          ? 'Camera permission was refused. Allow it in the browser, or type the code below.'
          : name === 'NotFoundError'
            ? 'No camera on this device. Use a handheld scanner or type the code.'
            : 'Could not start the camera. Type the code below.',
      );
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopCamera]);

  /** Decode frames until told to stop. Native first, zxing only if we must. */
  function startDecoding(stream: MediaStream) {
    const Native = nativeDetector();

    if (Native) {
      const detector = new Native({
        // QR is what gets engraved on the nameplate; the rest are what a printed
        // label or an older asset tag might carry.
        formats: ['qr_code', 'code_128', 'code_39', 'data_matrix', 'ean_13'],
      });
      let raf = 0;
      let stopped = false;
      let busy = false;

      const tick = () => {
        if (stopped) return;
        raf = requestAnimationFrame(tick);
        const video = videoRef.current;
        // `busy` matters: detect() is slower than a frame, and queueing one per
        // frame builds a backlog that ends with the phone hot and the UI stuck.
        if (busy || !video || video.readyState < 2) return;
        busy = true;
        detector
          .detect(video)
          .then((codes) => {
            const value = codes[0]?.rawValue;
            if (value) accept(value);
          })
          .catch(() => undefined)
          .finally(() => {
            busy = false;
          });
      };

      raf = requestAnimationFrame(tick);
      stopDecodeRef.current = () => {
        stopped = true;
        cancelAnimationFrame(raf);
      };
      return;
    }

    // No native decoder: fetch one. Lazily, so devices that never need it never
    // pay for it - and inside the precache, so it still works offline.
    let controls: { stop: () => void } | null = null;
    let cancelled = false;
    void import('@zxing/browser')
      .then(({ BrowserMultiFormatReader }) => {
        if (cancelled || !videoRef.current) return;
        const reader = new BrowserMultiFormatReader();
        return reader
          .decodeFromStream(stream, videoRef.current, (result) => {
            if (result) accept(result.getText());
          })
          .then((c) => {
            if (cancelled) c.stop();
            else controls = c;
          });
      })
      .catch(() => {
        setCameraError('Could not load the code reader. Type the code below.');
      });

    stopDecodeRef.current = () => {
      cancelled = true;
      controls?.stop();
    };
  }

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    try {
      // `torch` is real and widely supported on Android, and absent from the DOM
      // typings. Casting through unknown rather than pretending otherwise.
      await track.applyConstraints({
        advanced: [{ torch: next }],
      } as unknown as MediaTrackConstraints);
      setTorchOn(next);
    } catch {
      setTorchable(false); // it claimed a torch and has none; stop offering it
    }
  }

  // Freeing the camera while a result is on screen is deliberate: it stops the
  // decoder firing at the mould still sitting in frame, and it is the single
  // biggest thing anyone can do for the battery on a shared handheld.
  useEffect(() => {
    if (paused && cameraOn) stopCamera();
  }, [paused, cameraOn, stopCamera]);

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <ScanLine className="size-4 text-primary" aria-hidden />
          Scan a mould
        </h2>
        {cameraOn ? (
          <div className="flex gap-2">
            {torchable ? (
              <Button
                variant={torchOn ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => void toggleTorch()}
                aria-pressed={torchOn}
              >
                <Flashlight className="size-4" /> Light
              </Button>
            ) : null}
            <Button variant="secondary" size="sm" onClick={stopCamera}>
              <CameraOff className="size-4" /> Stop
            </Button>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => void startCamera()}>
            <Camera className="size-4" /> Use camera
          </Button>
        )}
      </div>

      {cameraOn ? (
        <div className="relative mb-3 overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} className="aspect-[4/3] w-full object-cover" playsInline muted />
          {/* A frame to aim with. Purely decorative, hence hidden from readers. */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden
          >
            <div className="h-40 w-40 rounded-lg border-2 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>
        </div>
      ) : null}

      {cameraError ? (
        <p className="mb-3 rounded-lg bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
          {cameraError}
        </p>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          accept(manual);
          setManual('');
        }}
        className="flex gap-2"
      >
        <Input
          value={manual}
          data-scan-manual="true"
          onChange={(e) => setManual(e.target.value)}
          placeholder="Or type / scan the mould code…"
          aria-label="Mould code"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="flex-1"
        />
        <Button type="submit" disabled={!manual.trim()}>
          Find
        </Button>
      </form>

      <p className="mt-2 flex items-center gap-1.5 text-xs text-subtle-foreground">
        <Keyboard className="size-3.5" aria-hidden />
        {wedged
          ? 'Handheld scanner detected - just scan, no need to click first.'
          : 'A handheld scanner works anywhere on this page without clicking first.'}
      </p>
    </div>
  );
}
